import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { postOrQueue } from "@/lib/offlineSync";
import { getPreviewWithFallback } from "@/lib/smartReviewCache";
import OfflineSyncBanner from "@/components/OfflineSyncBanner";
import ExitConfirmDialog from "@/components/ExitConfirmDialog";
import { useExitGuard } from "@/hooks/useExitGuard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ChevronLeft, CheckCircle2, XCircle, ArrowRight, Loader2, CloudOff, Trophy, RefreshCw,
} from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDisplayChoices(q) {
  const labels = ["A", "B", "C", "D"];
  const mapped = labels.map((l) => ({
    original: l.toLowerCase(),
    text: q[`choice_${l.toLowerCase()}`] || "",
  })).filter((c) => c.text);
  return shuffle(mapped).map((c, i) => ({ ...c, display: labels[i] }));
}

export default function SmartReviewOffline() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [source, setSource] = useState("none");
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ c: 0, t: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // FSRS time-to-answer tracker for Exam-style cached Smart Review.
  const shownAtRef = useRef(Date.now());

  const inProgress = !done && questions.length > 0 && !loading;
  const { confirmOpen, confirmExit, cancelExit } = useExitGuard(inProgress);
  useEffect(() => { shownAtRef.current = Date.now(); }, [idx, questions.length]);

  const load = async () => {
    setLoading(true);
    const { data, source } = await getPreviewWithFallback(15);
    if (!data?.questions?.length) {
      setQuestions([]); setSource("none");
    } else {
      const enriched = data.questions.map((q) => ({ ...q, displayChoices: buildDisplayChoices(q) }));
      setQuestions(enriched); setSource(source);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load();
      toast.success("Refreshed cached questions");
    } finally { setRefreshing(false); }
  };

  const current = questions[idx];
  const correctDisplay = current ? current.displayChoices.find((c) => c.original === (current.correct_answer || "").toLowerCase()) : null;

  const submit = async () => {
    if (!selected || !current) return;
    setSubmitting(true);
    try {
      // Grade locally using the cached correct answer
      const isCorrect = selected.original.toUpperCase() === current.correct_answer.toUpperCase();
      setResult({ is_correct: isCorrect, correctDisplay });
      setScore((s) => ({ c: s.c + (isCorrect ? 1 : 0), t: s.t + 1 }));

      // Queue the answer via Phase 21's postOrQueue — uses per-question endpoint
      // (no session_id needed). When online, it submits immediately; when offline,
      // it queues to IndexedDB and replays on reconnect.
      const r = await postOrQueue(api, "/student/answer", {
        question_id: current.id,
        selected_answer: selected.original.toUpperCase(),
        time_taken_ms: Math.max(0, Date.now() - shownAtRef.current),
      });
      if (r.queued) {
        toast.info("Answer saved offline — will sync when reconnected", { duration: 3000 });
      } else if (!r.ok) {
        toast.error("Could not record answer");
      }
    } finally { setSubmitting(false); }
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setDone(true);
      return;
    }
    setIdx((i) => i + 1); setSelected(null); setResult(null);
  };

  const restart = async () => {
    setIdx(0); setSelected(null); setResult(null); setScore({ c: 0, t: 0 }); setDone(false);
    await load();
  };

  if (loading) {
    return <div className="text-slate-500" data-testid="smart-offline-loading">Loading cached questions…</div>;
  }

  if (!current && !done) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="smart-offline-empty">
        <Link to="/student/smart-review" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back</Link>
        <Card className="p-10 text-center border-slate-200">
          <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 grid place-items-center text-slate-400 mb-4">
            <CloudOff className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-2xl font-bold">No cached questions yet</h2>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">
            Open Smart Review while online once, or enable Background Pre-fetch in your Account settings, so PrepMasters can keep a study set ready for offline practice.
          </p>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="smart-offline-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Try again
            </Button>
            <Button onClick={() => nav("/student/account")} className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Open Account Settings</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="smart-offline-complete">
        <Card className="p-10 border-slate-200 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 grid place-items-center text-emerald-700 mb-4">
            <Trophy className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-3xl font-bold">Offline Practice Complete</h2>
          <p className="text-slate-600 mt-2">
            You scored <span className="font-bold text-slate-900">{score.c}/{score.t}</span>. Answers will sync to your mastery the moment you reconnect.
          </p>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Button variant="outline" onClick={restart} data-testid="smart-offline-restart-btn">Restart with new set</Button>
            <Link to="/student/smart-review"><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="smart-offline-back-btn">Back to Smart Review</Button></Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="smart-offline-session">
      <Link to="/student/smart-review" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Exit</Link>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 flex items-center gap-2">
            <CloudOff className="w-3.5 h-3.5" /> Offline Practice · Q {idx + 1} of {questions.length}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {source === "cache" ? "Using cached questions" : "Fresh from network · cached for offline"}
          </div>
        </div>
        <div className="text-sm text-slate-600">Score: <span className="font-semibold text-slate-900">{score.c}/{score.t}</span></div>
      </div>
      <Progress value={(idx / questions.length) * 100} className="h-2 [&>div]:bg-amber-500" />

      <OfflineSyncBanner />

      <Card className="p-6 sm:p-8 border-slate-200">
        {current.priority_reasons?.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {current.priority_reasons.map((r) => (
              <Badge key={r} variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">{r}</Badge>
            ))}
          </div>
        )}
        <p className="text-xl leading-relaxed text-slate-900 font-medium">{current.question_text}</p>

        <div className="mt-6 space-y-2">
          {current.displayChoices.map((c) => {
            const isPicked = selected?.original === c.original;
            const isCorrect = result && c.original === current.correct_answer.toLowerCase();
            const isWrongPick = result && isPicked && !result.is_correct;
            let cls = "border-slate-200 hover:border-slate-300";
            if (result) {
              if (isCorrect) cls = "border-emerald-500 bg-emerald-50";
              else if (isWrongPick) cls = "border-rose-500 bg-rose-50";
              else cls = "border-slate-200 opacity-60";
            } else if (isPicked) {
              cls = "border-amber-500 bg-amber-50";
            }
            return (
              <button
                key={c.display}
                type="button"
                disabled={!!result}
                onClick={() => setSelected(c)}
                className={`w-full text-left p-3.5 rounded-md border-2 transition-colors ${cls}`}
                data-testid={`smart-offline-choice-${c.display}`}
              >
                <span className="font-bold text-sm w-7 inline-block">{c.display}.</span>
                <span className="text-sm text-slate-800">{c.text}</span>
              </button>
            );
          })}
        </div>

        {!result ? (
          <Button
            className="mt-6 w-full h-11 bg-amber-600 hover:bg-amber-700"
            disabled={!selected || submitting}
            onClick={submit}
            data-testid="smart-offline-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Submit Answer
          </Button>
        ) : (
          <>
            <div className={`mt-6 p-4 rounded-md border ${result.is_correct ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <div className="flex items-center gap-2 font-semibold">
                {result.is_correct ? (
                  <><CheckCircle2 className="w-4 h-4 text-emerald-700" /><span className="text-emerald-800 text-sm">Correct</span></>
                ) : (
                  <><XCircle className="w-4 h-4 text-rose-700" /><span className="text-rose-800 text-sm">Not quite — correct answer was <span className="font-bold">{correctDisplay?.display}</span></span></>
                )}
              </div>
            </div>
            <Button
              className="mt-4 w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF]"
              onClick={next}
              data-testid="smart-offline-next-btn"
            >
              {idx + 1 >= questions.length ? "Finish" : "Next Question"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
      </Card>

      <ExitConfirmDialog
        open={confirmOpen}
        onCancel={cancelExit}
        onConfirm={confirmExit}
        mode="smart-review"
      />
    </div>
  );
}
